import { ArrowRightIcon } from "@talladega/icons";
import classNames from "classnames";
import { useState } from "react";
import CloudflareImagesImage from "@common/components/CloudflareImagesImage";
import TradeFindVehicleForm, {
  TradeFindVehicleFormInput,
} from "@modules/trade/components/TradeFindVehicleForm";
import { useForm } from "react-hook-form";
import { useRouter } from "next/router";
import { useNumberMask } from "@common/hooks/useMask";
import useUtmParamsFromQuery from "@modules/query-string/hooks/useUtmParamsFromQuery";
import { CmsHeading } from "@talladega/cms";
import { GetBackgroundStyle, IsNodeStructure } from "@talladega/helpers";
import { ActionButton } from "@talladega/types";
import CmsButtonModalContainer from "./CmsButtonModalContainer";
import CmsButton from "./CmsButton";
import { HeroHeadingFormLayout } from "../types/HeroHeadingFormLayout";

const HeroHeadingForm = ({
  heroImage,
  sideContent,
  sideContentPosition,
  button,
  background,
  htmlIdRef,
  heroHeading,
  heroSubheading,
  formContentPosition = "Bottom",
  imageContentPosition = "Top",
}: HeroHeadingFormLayout) => {
  const router = useRouter();
  const locale = router.locale || "en";
  const { unMaskPipe: unmaskNumberPipe } = useNumberMask();
  const [buttonModalState, setButtonModalState] = useState<{
    show: boolean;
    actionType: ActionButton;
  }>({ show: false, actionType: ActionButton.UnAssigned });

  const { altText, sourceUrl } =
    (IsNodeStructure(heroImage) ? heroImage.node : heroImage) ?? {};

  const icoForm = useForm<TradeFindVehicleFormInput>();
  const utmParams = useUtmParamsFromQuery();

  const handleIcoSubmit = icoForm.handleSubmit(
    (data: TradeFindVehicleFormInput) => {
      let icoData: Record<string, unknown> = {
        province: data.province?.abbr,
        odometer: unmaskNumberPipe(data.mileage),
      };

      if (data.vin) {
        icoData = {
          ...icoData,
          vin: data.vin,
        };
      }

      if (data.vehicle) {
        icoData = {
          ...icoData,
          vehicle: data.vehicle,
          trim: data.vehicle.trim,
        };
      }

      window.acIco.destroy();
      window.acIco.initModal("ico", "throttle", {
        data: icoData,
        locale,
        utmParams: {
          utmSource: utmParams.utmSource ?? button?.icoModal?.utmSource,
          utmMedium: utmParams.utmMedium ?? button?.icoModal?.utmMedium,
          utmCampaign: utmParams.utmCampaign ?? button?.icoModal?.utmCampaign,
          utmContent: utmParams.utmContent ?? button?.icoModal?.utmContent,
        },
      });
    }
  );

  const handleOnEnterVehicleManuallyClick = () => {
    const icoData: Record<string, unknown> = {
      manualSearch: true,
    };

    window.acIco.destroy();
    window.acIco.initModal("ico", "throttle", {
      data: icoData,
      locale,
      utmParams: {
        utmSource: utmParams.utmSource ?? button?.icoModal?.utmSource,
        utmMedium: utmParams.utmMedium ?? button?.icoModal?.utmMedium,
        utmCampaign: utmParams.utmCampaign ?? button?.icoModal?.utmCampaign,
        utmContent: utmParams.utmContent ?? button?.icoModal?.utmContent,
      },
    });
  };

  const handleCmsButtonClick = () => {
    if (button?.actionButton !== ActionButton.IcoModal) return;

    handleIcoSubmit();
  };

  const baseDomain = process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGES_DOMAIN || "";

  return (
    <div
      id="cms-hero-heading"
      className="no-scrollbar relative overflow-visible bg-white bg-cover bg-center"
      style={GetBackgroundStyle(background, baseDomain)}
    >
      <div
        className={classNames(
          "relative mx-auto flex gap-10 max-w-page lg:flex-row items-center px-4 pb-16 pt-10 lg:px-5 lg:py-16",
          {
            "flex-col":
              (sideContent === "image" && imageContentPosition === "Bottom") ||
              (sideContent === "form" && formContentPosition === "Bottom"),
            "flex-col-reverse":
              (sideContent === "image" && imageContentPosition === "Top") ||
              (sideContent === "form" && formContentPosition === "Top"),
            "lg:flex-row": sideContentPosition === "Right",
            "lg:flex-row-reverse": sideContentPosition === "Left",
          }
        )}
      >
        <div
          className={classNames("flex-1 md:px-28 lg:px-0", {
            "lg:basis-[40%]": sideContent === "image",
            "lg:basis-[50%] lg:pr-28": sideContent === "form",
          })}
        >
          <div>
            <div className="w-full text-center sm:items-start sm:justify-start sm:text-left">
              {heroHeading && (
                <CmsHeading
                  headingLevel={heroHeading.headingGroup?.headingLevel}
                  textSize={heroHeading.headingGroup?.textSize}
                  text={heroHeading.headingGroup?.text}
                  htmlId={heroHeading.headingGroup?.htmlId}
                />
              )}
            </div>

            <div className="mt-2 w-full items-center justify-center text-center sm:items-start sm:justify-start sm:text-left">
              {heroSubheading && (
                <div className="mt-4 lg:mt-8">
                  <CmsHeading
                    headingLevel={heroSubheading.headingGroup?.headingLevel}
                    textSize={heroSubheading.headingGroup?.textSize}
                    text={heroSubheading.headingGroup?.text}
                    htmlId={heroSubheading.headingGroup?.htmlId}
                  />
                </div>
              )}
            </div>

            {button && !button.isHidden && sideContent === "image" && (
              <div className="mt-8 items-center justify-center text-center sm:items-start sm:justify-start sm:text-left">
                <CmsButton
                  button={button}
                  rightIcon={<ArrowRightIcon />}
                  setButtonModalState={setButtonModalState}
                  htmlIdRef={htmlIdRef}
                  onClick={handleCmsButtonClick}
                />
              </div>
            )}
          </div>
        </div>
        <div
          className={classNames("relative w-full flex-1 lg:pb-0", {
            "lg:basis-[60%]": sideContent === "image",
            "lg:basis-[50%]": sideContent === "form",
          })}
        >
          {sideContent === "image" ? (
            <div className="relative inset-0 h-auto min-w-full">
              <div className="relative h-56 overflow-auto md:h-96">
                {heroImage && sourceUrl && (
                  <CloudflareImagesImage
                    src={sourceUrl}
                    alt={altText}
                    fill
                    className="z-[2] h-auto w-full rounded-lg object-contain"
                  />
                )}
              </div>
            </div>
          ) : (
            <TradeFindVehicleForm
              themeClassNames="throttle"
              form={icoForm}
              button={
                button && (
                  <CmsButton
                    className="w-full"
                    button={button}
                    setButtonModalState={setButtonModalState}
                    htmlIdRef={htmlIdRef}
                    onClick={handleCmsButtonClick}
                  />
                )
              }
              onEnterVehicleManuallyClick={handleOnEnterVehicleManuallyClick}
            />
          )}
        </div>
      </div>
      {buttonModalState.show && (
        <CmsButtonModalContainer buttonModalState={buttonModalState} />
      )}
    </div>
  );
};

export default HeroHeadingForm;
